import React, { useState } from "react";
import { Share2, X as XIcon } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";

// Reusable share menu — used on the home grid, profile grid, video page,
// and the map's expanded player. Builds the platform-specific share intent URL,
// pops a new tab, and pings POST /videos/{id}/share so the producer earns
// SHARE_REWARD credits (one reward per user × video × platform).
//
// Usage:
//   <ShareButton video={v} compact />            -- icon only, for tile grids
//   <ShareButton video={v} />                    -- full button, for player pages
const PLATFORMS = [
  {
    key: 'x',
    label: 'X',
    color: 'bg-gray-900 hover:bg-gray-800 text-white border border-gray-700',
    intent: ({ url, text, hashtags }) =>
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}&hashtags=${encodeURIComponent(hashtags)}&via=GlobalEyesDOTtv`,
    icon: () => <XIcon className="h-3.5 w-3.5" />,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    color: 'bg-blue-600 hover:bg-blue-500 text-white',
    intent: ({ url }) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    icon: () => <span className="text-[14px] font-black leading-none">f</span>,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    color: 'bg-sky-700 hover:bg-sky-600 text-white',
    intent: ({ url }) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    icon: () => <span className="text-[11px] font-black leading-none">in</span>,
  },
];

const ShareButton = ({ video, compact = false }) => {
  const { refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  if (!video) return null;
  const videoUrl = `${window.location.origin}/video/${video.id}`;
  // For X, attribute the platform + (when known) the producer's username so
  // viewers can click through to follow them on @GlobalEyesDOTtv style flow.
  const producer = video.username ? `@${video.username}` : 'a real human';
  const text = `${video.title || 'Watch this'} on GlobalEyes.TV — by ${producer} · via @GlobalEyesDOTtv`;
  // Hashtag string adds discoverability into X's hashtag streams. Keep tight.
  const hashtags = ['GlobalEyes', 'MadeByHumans'].join(',');

  const onShare = async (platform) => {
    setBusyKey(platform.key);
    // Open the share intent immediately (browsers block popups otherwise)
    try {
      window.open(platform.intent({ url: videoUrl, text, hashtags }), '_blank', 'noopener,noreferrer');
    } catch (err) { /* ignore */ }
    // Then call our backend to log the share + reward the producer
    try {
      // apiClient handles the auth header automatically when a token
      // is present in localStorage. Anonymous shares still hit the
      // endpoint (no rewards) since /videos/{id}/share is public.
      const res = await api.post(
        `/videos/${video.id}/share`,
        { platform: platform.key },
      );
      if (res.data?.rewarded) {
        toast.success(`Producer earned +${(res.data.reward_credits || 0).toFixed(2)} cr · thanks for sharing!`);
        if (refreshUser) refreshUser();
      } else {
        toast(res.data?.message || 'Share logged.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't log share");
    }
    setBusyKey(null);
    setOpen(false);
  };

  const triggerLabel = compact ? null : 'Share';

  return (
    <div className="relative inline-block" onMouseLeave={() => setOpen(false)}>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className={`inline-flex items-center gap-1.5 ${
          compact
            ? 'p-1.5 rounded-md bg-gray-800/70 hover:bg-gray-700 text-gray-300'
            : 'px-3 py-1.5 rounded-md bg-gray-800 hover:bg-gray-700 text-gray-200 text-xs font-semibold'
        } transition-colors`}
        data-testid={`share-btn-${video.id}`}
        title="Share — producer earns credits"
      >
        <Share2 className={compact ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
        {triggerLabel}
      </button>
      {open && (
        <div
          className="absolute z-[3000] right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg p-1 shadow-xl flex flex-col min-w-[140px]"
          data-testid={`share-menu-${video.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {PLATFORMS.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={(e) => { e.stopPropagation(); onShare(p); }}
              disabled={busyKey === p.key}
              className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-xs transition-colors disabled:opacity-60 ${p.color}`}
              data-testid={`share-${p.key}-btn`}
            >
              <p.icon />
              <span>{busyKey === p.key ? 'Opening…' : `Share on ${p.label}`}</span>
            </button>
          ))}
          <p className="text-gray-500 text-[10px] px-2 py-1 italic">+0.25 cr to producer · once per platform</p>
        </div>
      )}
    </div>
  );
};

export default ShareButton;
